import { Fragment, memo, useEffect, useMemo, useState } from 'react';
import { SubjectsWizardUseCase } from 'shell/models/subjects-wizard';
import Subject from '../subject';
import { LastStepVM, ParentVM, SectionVM } from 'civil-status/view-models/step-form';
import { CodeSubject } from 'civil-status/constants/code-subject-section';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import { FormikProps } from 'formik';
import { ID_NUMBERS_COMPARENTI } from '../section/list/list-number';
import { subjectIsVisibility } from 'civil-status/utils/subject-is-visibility';
import { KeySections, KeySubjects } from 'civil-status/constants/keys-subjects-section';
import { ConfigurationType } from 'civil-status/constants/configuration-type';
import { withInterprete } from 'civil-status/utils/with-interprete';

interface SubjectsProps {
  formContext: FormikProps<LastStepVM>;
  idEvento?: number;
  onSaveSubject: (values: ParentVM) => ResponsePromise<number | undefined> | undefined | void;
  soggettiUseCaseData?: Array<SubjectsWizardUseCase>;
  disabled?: boolean;
  dateFormationAct?: string;
  tipoEvento?: string;
  withReasonDelay?: boolean;
  isStepFromEmergency?: boolean;
  isLastStepEmergency?: boolean;
  setDataInterprete: (isWithInterprete?: boolean) => void;
  controllIntNasCivilStatus: (value?: string) => void;
  controllDecedutoCivilStatus: (value?: string) => void;
  controllIsChangeSurnameName: (isChangeSurnameName?: boolean) => void;
}

const SubjectsComponent = ({
  formContext,
  onSaveSubject,
  soggettiUseCaseData,
  idEvento,
  tipoEvento,
  disabled,
  dateFormationAct,
  withReasonDelay,
  isStepFromEmergency,
  isLastStepEmergency,
  setDataInterprete,
  controllIntNasCivilStatus,
  controllDecedutoCivilStatus,
  controllIsChangeSurnameName,
}: SubjectsProps): React.ReactElement => {
  const [soggettiUsecaseDataArr, setSoggettiUsecaseDataArr] = useState<
    Array<SubjectsWizardUseCase>
  >([]);

  // values sezione dati trascrizione
  const valuesDatiTrascrizione: SectionVM | undefined = useMemo(() => {
    return (
      formContext.values[KeySections.keyDatiTrascrizione] ||
      formContext.values[KeySections.keyDatiTrascrizioneTpRichiedente] ||
      formContext.values[KeySections.keyDatiTrascrizioneTpRichiedenteFg]
    );
  }, [
    formContext.values[KeySections.keyDatiTrascrizione],
    formContext.values[KeySections.keyDatiTrascrizioneTpRichiedente],
    formContext.values[KeySections.keyDatiTrascrizioneTpRichiedenteFg],
  ]);

  // values sezione Dati morte
  const valuesDatiEventoCittadinanza: SectionVM | undefined = useMemo(() => {
    return formContext.values[KeySections.keyDatiSpecificiCittadinanza];
  }, [formContext.values[KeySections.keyDatiSpecificiCittadinanza]]);

  // values sezione Dati morte
  const valuesDatiDecesso: SectionVM | undefined = useMemo(() => {
    return formContext.values[KeySections.keyDatiDecesso];
  }, [formContext.values[KeySections.keyDatiDecesso]]);

  const dataTranscriptionBirth: SectionVM | undefined = useMemo(() => {
    return formContext.values[KeySections.keyTrascrizioneNascita];
  }, [formContext.values[KeySections.keyTrascrizioneNascita]]);

  // nella sezione scelto 1 comparente
  const isWith1Comparente: boolean = useMemo(() => {
    return valuesDatiDecesso?.numberAppearing === ID_NUMBERS_COMPARENTI.ID_COMPARENTE1;
  }, [valuesDatiDecesso?.numberAppearing]);

  // nella sezione scelti 2 comparenti
  const isWith2Comparente: boolean = useMemo(() => {
    return valuesDatiDecesso?.numberAppearing === ID_NUMBERS_COMPARENTI.ID_COMPARENTE2;
  }, [valuesDatiDecesso?.numberAppearing]);

  // values soggetto Deceduto
  const valuesDeceduto: ParentVM | undefined = useMemo(() => {
    return formContext.values[KeySubjects.keyDeceduto];
  }, [formContext.values[KeySubjects.keyDeceduto]]);

  // values soggetto Intestatrio (Cittadinanza)
  const valuesIntest: ParentVM | undefined = useMemo(() => {
    return formContext.values[KeySubjects.keyIntest];
  }, [formContext.values[KeySubjects.keyIntest]]);

  // values soggetto Intestatrio Nascita
  const valuesIntNas: ParentVM | undefined = useMemo(() => {
    return (
      formContext.values[KeySubjects.keyIntNas] ||
      formContext.values[KeySubjects.keyIntNas2] ||
      formContext.values[KeySubjects.keyIntNas2C1]
    );
  }, [
    formContext.values[KeySubjects.keyIntNas],
    formContext.values[KeySubjects.keyIntNas2],
    formContext.values[KeySubjects.keyIntNas2C1],
  ]);

  // values soggetto Intestatrio 1
  const valuesIntest1: ParentVM | undefined = useMemo(() => {
    return formContext.values[KeySubjects.keyIntest1];
  }, [formContext.values[KeySubjects.keyIntest1]]);

  // values soggetti dichiaranti
  const dichiaranti: Array<ParentVM> | undefined = useMemo(() => {
    const keysDichiarante: Array<string> | undefined = Object.keys(formContext.values || {}).filter(
      (itemKey) => (formContext.values[itemKey] as ParentVM)?.flagDichiarante === '1'
    ) as Array<string> | undefined;

    if ((keysDichiarante || []).length > 0) {
      const res: Array<ParentVM> = [];
      keysDichiarante?.forEach((itemKeyDichiarante) => {
        res.push(formContext.values[itemKeyDichiarante] as ParentVM);
      });

      return res;
    }

    return undefined;
  }, [formContext.values]);

  // values sezione Genitore che trasmette cittadinanza
  const valuesGenitoreTrasmetteCittadinanza: SectionVM | undefined = useMemo(() => {
    return formContext.values[KeySections.keyGenitCheTrasmetteCit];
  }, [formContext.values[KeySections.keyGenitCheTrasmetteCit]]);

  // values sezione Richiesta Genitore Italiano
  const valuesRicGenitoreItaliano: SectionVM | undefined = useMemo(() => {
    return formContext.values[KeySections.keyRicGenitoreItaliano];
  }, [formContext.values[KeySections.keyRicGenitoreItaliano]]);

  // values sezione Dati Specifici Di Cittadinanza
  const valuesDatiSpecificiCittadinanza: SectionVM | undefined = useMemo(() => {
    return formContext.values[KeySections.keyDatiSpecificiCittadinanza];
  }, [formContext.values[KeySections.keyDatiSpecificiCittadinanza]]);

  // values Dati generali consenso
  const valuesDatiGeneraliConsenso: SectionVM | undefined = useMemo(() => {
    return formContext.values[KeySections.keyDatiGeneraliRicoGen];
  }, [formContext.values[KeySections.keyDatiGeneraliRicoGen]]);

  const valuesSpecificCivilUnionMarriage: SectionVM = useMemo(() => {
    return (
      formContext.values[KeySections.keyDatiSpecificiUniciv] ||
      formContext.values[KeySections.keyDatiSpecificiMatr] ||
      {}
    );
  }, [
    formContext.values[KeySections.keyDatiSpecificiUniciv],
    formContext.values[KeySections.keyDatiSpecificiMatr],
  ]);

  const valuesDatiSepar: SectionVM = useMemo(() => {
    return (
      formContext.values[KeySections.keyDatiSepar] ||
      formContext.values[KeySections.keyDatiTrascrizioneSeparazione] ||
      formContext.values[KeySections.keyDatiSeparUC] ||
      {}
    );
  }, [
    formContext.values[KeySections.keyDatiSepar],
    formContext.values[KeySections.keyDatiTrascrizioneSeparazione],
    formContext.values[KeySections.keyDatiSeparUC],
  ]);

  useEffect(() => {
    if ((soggettiUseCaseData || [])?.length === 0) {
      return;
    }

    setSoggettiUsecaseDataArr(
      (soggettiUseCaseData || []).filter((itemSubject) => {
        const isWithDeceduto = Boolean(
          soggettiUseCaseData?.find((item) => item.codSoggetto === CodeSubject.DECEDUTO)
        );

        const isWithInterprete = withInterprete(formContext.values);

        return subjectIsVisibility(
          itemSubject,
          isWithInterprete,
          isWith1Comparente,
          isWith2Comparente,
          isWithDeceduto,
          valuesDeceduto,
          valuesDatiTrascrizione,
          valuesGenitoreTrasmetteCittadinanza,
          valuesDatiSpecificiCittadinanza,
          valuesRicGenitoreItaliano,
          valuesDatiGeneraliConsenso,
          valuesIntNas?.configuration === ConfigurationType.BASESCATTO ? valuesIntNas : undefined,
          valuesSpecificCivilUnionMarriage,
          valuesDatiSepar,
          valuesIntest1
        );
      })
    );
  }, [
    soggettiUseCaseData,
    isWith1Comparente,
    isWith2Comparente,
    dichiaranti,
    valuesDeceduto,
    valuesDatiDecesso,
    valuesDatiEventoCittadinanza,
    valuesIntest,
    valuesDatiTrascrizione,
    valuesGenitoreTrasmetteCittadinanza,
    valuesDatiSpecificiCittadinanza,
    valuesRicGenitoreItaliano,
    valuesDatiGeneraliConsenso,
    valuesIntNas,
    valuesSpecificCivilUnionMarriage,
    valuesDatiSepar,
    valuesIntest1,
    dataTranscriptionBirth,
  ]);

  return (
    <Fragment>
      {soggettiUsecaseDataArr.map((itemSubject) => {
        const keySubject = `subject-${itemSubject?.codSoggetto}`;

        if (itemSubject?.flagValidita === '0') {
          // se il flag è spento non mostrare accordion
          return <></>;
        }

        return (
          <Subject
            formContext={formContext}
            title={itemSubject?.nomeSoggetto}
            keySubject={keySubject}
            configuration={itemSubject?.configurazione?.trim()}
            tipoSoggetto={itemSubject?.tipoSoggetto}
            presenza={itemSubject.presenza}
            key={keySubject}
            onSaveSubject={onSaveSubject}
            codeSubject={itemSubject?.codSoggetto}
            codUseCase={itemSubject?.key?.codUseCase}
            flagDichiarante={itemSubject?.flagDichiarante}
            flagFirmatario={itemSubject?.flagFirmatario}
            withoutSearch={
              itemSubject?.configurazione === ConfigurationType.NASC01 ||
              itemSubject?.configurazione === ConfigurationType.NASC02 ||
              itemSubject?.configurazione === ConfigurationType.NASC03 ||
              itemSubject?.configurazione === ConfigurationType.NASC04 ||
              itemSubject?.configurazione === ConfigurationType.NASC05 ||
              itemSubject?.configurazione === ConfigurationType.NASC06 ||
              itemSubject?.configurazione === ConfigurationType.NASC99
            }
            idEvento={idEvento}
            disabled={disabled}
            dateFormationAct={dateFormationAct}
            withReasonDelay={withReasonDelay}
            setDataInterprete={setDataInterprete}
            controllIntNasCivilStatus={controllIntNasCivilStatus}
            controllDecedutoCivilStatus={controllDecedutoCivilStatus}
            controllIsChangeSurnameName={controllIsChangeSurnameName}
            isStepFromEmergency={isStepFromEmergency}
            isLastStepEmergency={isLastStepEmergency}
            tipoEvento={tipoEvento}
          />
        );
      })}
    </Fragment>
  );
};

SubjectsComponent.displayName = 'Subjects Component';

const Subjects = memo(SubjectsComponent);

export default Subjects;
